/**
 * screens/settings/Settings.tsx — opinionated presets + a few safe switches.
 * Presets (preview before install), per-motion toggles, channel caps, DPDP
 * retention + erasure, and the Tier-2 ignored-weight slider. UI only — every
 * change goes through the settings / presets services.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';

import { LightColors as C, Radii as R, FontSize as FS, FontWeight as FW } from '../../theme/theme';
import { Txt, Row, Stack, Eyebrow } from '../../components/ui';
import { Card, CardHead, CardBody } from '../../components/card';
import Button from '../../components/button';
import Chip from '../../components/chip';
import Switch from '../../components/switch';
import Alert from '../../components/alert';
import Spinner from '../../components/spinner';
import Stepper from '../../components/stepper';
import Slider from '../../components/slider';
import Select from '../../components/select';
import TextField from '../../components/input';
import AppModal from '../../components/modal';
import Icon, { IconName } from '../../components/icon';
import { toneColors } from '../../theme/tones';
import { Page, PageHeader } from '../_layout';

import { Settings as SettingsModel } from '../../schema/settings';
import { PresetSummary, PresetDetail } from '../../schema/preset';
import { getSettings, updateSettings, eraseContact } from '../../services/settings/settings';
import { listPresets, getPreset, installPreset } from '../../services/presets/presets';
import { Seed } from '../../services/_demo/seed';
import { getDemoVertical } from '../../services/_demo/verticalStore';
import { MOTIONS, MotionKey, FamilyKey, STRATEGY_SHORT, motionMeta } from '../../domain/motions';
import { useToast } from '../../components/toast';
import { handleApiError } from '../../utils/errorHandler';
import { useCounts } from '../../context/countsContext';
import { useApp } from '../../context/appContext';

const RETENTION_OPTIONS = [
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '365 days' },
  { value: '730', label: '730 days' },
];

const FAMILY_ORDER: FamilyKey[] = ['Upsell', 'Cross-sell', 'Service', 'Nudge', 'Down-sell'];

/** Premium section header: a soft icon tile + title, passed as CardHead children. */
function SectionHead({ icon, title }: { icon: IconName; title: string }) {
  return (
    <Row gap={10} style={{ alignItems: 'center' }}>
      <View
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: C.paper2,
          borderWidth: 1,
          borderColor: C.line,
        }}
      >
        <Icon name={icon} size={15} color={C.ink2} />
      </View>
      <Txt size={FS.md} weight={FW.semibold} color={C.ink}>
        {title}
      </Txt>
    </Row>
  );
}

export default function Settings() {
  const toast = useToast();
  const counts = useCounts();
  const { vertical } = useApp();

  const [settings, setSettings] = useState<SettingsModel | null>(null);
  const [presets, setPresets] = useState<PresetSummary[]>([]);
  const [installed, setInstalled] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewDetail, setPreviewDetail] = useState<PresetDetail | null>(null);

  const [erasureOpen, setErasureOpen] = useState(false);
  const [erasureContact, setErasureContact] = useState('');
  const [erasureDone, setErasureDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([getSettings(), listPresets()]);
      setSettings(s);
      setPresets(p);
      setInstalled(new Set(Seed.installed(getDemoVertical())));
    } catch (e) {
      handleApiError(e, 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load, vertical]),
  );

  const persist = async (patch: Partial<SettingsModel>, toastMsg?: string) => {
    setSettings((s) => (s ? { ...s, ...patch } : s));
    try {
      await updateSettings(patch);
      if (toastMsg) toast.success(toastMsg);
    } catch (e) {
      handleApiError(e, 'Could not save settings');
    }
  };

  const toggleMotion = (k: MotionKey) => {
    if (!settings) return;
    const next = !settings.toggles[k];
    persist(
      { toggles: { ...settings.toggles, [k]: next } },
      `${MOTIONS[k].label} ${next ? 'turned on' : 'turned off'}`,
    );
  };

  const toggleHistoryFeed = () => {
    if (!settings) return;
    const next = !settings.toggles.history_feed;
    persist(
      { toggles: { ...settings.toggles, history_feed: next } },
      `History feed ${next ? 'turned on' : 'turned off'}`,
    );
  };

  const openPreview = async (key: string) => {
    setPreviewKey(key);
    try {
      setPreviewDetail(await getPreset(key));
    } catch (e) {
      handleApiError(e, 'Could not load preset');
    }
  };

  const closePreview = () => {
    setPreviewKey(null);
    setPreviewDetail(null);
  };

  const install = async (key: string, name: string) => {
    try {
      const res = await installPreset(key);
      setInstalled((prev) => new Set(prev).add(key));
      toast.success(`${name} installed`, `${res.installed} rules${res.deferred ? ` (${res.deferred} defer to your AI rules)` : ''}`);
      counts.refresh();
      closePreview();
    } catch (e) {
      handleApiError(e, 'Could not install preset');
    }
  };

  const confirmErasure = async () => {
    try {
      await eraseContact(erasureContact.trim());
      setErasureDone(true);
      setTimeout(() => {
        setErasureOpen(false);
        setErasureDone(false);
        setErasureContact('');
        toast.success('Contact PII erased from the ledger');
      }, 1100);
    } catch (e) {
      handleApiError(e, 'Could not erase contact');
    }
  };

  if (loading || !settings) {
    return (
      <Page>
        <PageHeader icon="gear" eyebrow="Configuration" title="Settings" subtitle="Opinionated presets and a few safe switches — by design." />
        <View style={{ paddingVertical: 56, alignItems: 'center' }}>
          <Spinner size={24} />
        </View>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        icon="gear"
        eyebrow="Configuration"
        title="Settings"
        subtitle="A few opinionated presets and safe switches — never a wall of algorithm knobs. The opinion is the product."
        style={{ marginBottom: 22 }}
      />

      {/* Presets */}
      <Card style={{ marginBottom: 20 }}>
        <CardHead right={<Txt size={FS.xs} color={C.ink4}>install a vertical bundle of pre-approved rules</Txt>}>
          <SectionHead icon="rules-list" title="Presets" />
        </CardHead>
        <CardBody>
          {presets.length === 0 ? (
            <View style={{ paddingVertical: 22, alignItems: 'center', gap: 4 }}>
              <Txt size={FS.md} weight={FW.medium} color={C.ink2}>
                No presets for this catalog yet
              </Txt>
              <Txt size={13} color={C.ink4} align="center">
                Preset bundles appear here once they're published for your vertical.
              </Txt>
            </View>
          ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
            {presets.map((p) => {
              const isInstalled = installed.has(p.key);
              return (
                <View
                  key={p.key}
                  style={{ flexGrow: 1, flexBasis: 300, minWidth: 260, borderWidth: 1, borderColor: C.line, borderRadius: R.r4, padding: 16, gap: 8 }}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Txt size={FS.md} weight={FW.semibold} color={C.ink}>
                      {p.name}
                    </Txt>
                    <Chip label={`${p.rule_count} rules`} />
                  </View>
                  <Txt size={13.5} color={C.ink3} lineHeight={20} style={{ flex: 1 }}>
                    {p.description}
                  </Txt>
                  <Row gap={8}>
                    <Button label={`View ${p.rule_count} rules`} size="sm" icon="eye" onPress={() => openPreview(p.key)} />
                    {isInstalled ? (
                      <Chip tone="success" leading={<Icon name="check" size={12} color={C.success} strokeWidth={2.6} />} label="Installed" />
                    ) : (
                      <Button label="Install" size="sm" variant="accent" onPress={() => install(p.key, p.name)} />
                    )}
                  </Row>
                </View>
              );
            })}
          </View>
          )}
        </CardBody>
      </Card>

      {/* Motions + right column */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
        {/* Motions */}
        <Card style={{ flexGrow: 1, flexBasis: 360, minWidth: 300 }}>
          <CardHead right={<Txt size={FS.xs} color={C.ink4}>enable / disable per family</Txt>}>
            <SectionHead icon="sparkle" title="Motions" />
          </CardHead>
          <CardBody>
            <Stack gap={18}>
              {FAMILY_ORDER.map((family) => {
                const items = (Object.keys(MOTIONS) as MotionKey[]).filter((k) => MOTIONS[k].family === family);
                const onCount = items.filter((k) => !!settings.toggles[k]).length;
                const dot = toneColors(motionMeta(items[0]).tone).fg;
                return (
                  <View key={family}>
                    <Row gap={8} style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <Row gap={7} style={{ alignItems: 'center' }}>
                        <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: dot }} />
                        <Eyebrow>{family}</Eyebrow>
                      </Row>
                      <Txt size={FS.xs} color={C.ink4}>
                        {onCount}/{items.length} on
                      </Txt>
                    </Row>
                    <Stack gap={11}>
                      {items.map((k) => (
                        <Switch
                          key={k}
                          value={!!settings.toggles[k]}
                          onValueChange={() => toggleMotion(k)}
                          label={MOTIONS[k].label}
                          labelSide="left"
                          spread
                        />
                      ))}
                    </Stack>
                  </View>
                );
              })}

              {/* History feed — learn from real acceptance/revenue history */}
              <View style={{ borderTopWidth: 1, borderTopColor: C.line, paddingTop: 16 }}>
                <Row gap={8} style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Row gap={7} style={{ alignItems: 'center' }}>
                    <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: C.accent }} />
                    <Eyebrow>LEARNING</Eyebrow>
                  </Row>
                  <Txt size={FS.xs} color={C.ink4}>
                    {settings.toggles.history_feed ? '1/1 on' : '0/1 on'}
                  </Txt>
                </Row>
                <Switch
                  value={!!settings.toggles.history_feed}
                  onValueChange={toggleHistoryFeed}
                  label="History feed"
                  labelSide="left"
                  spread
                />
                <Txt size={12.5} color={C.ink3} style={{ marginTop: 6 }}>
                  When on, AI proposals prioritise your highest-acceptance and highest-revenue items from real history. With no history yet, the AI reasons on its own.
                </Txt>
              </View>
            </Stack>
          </CardBody>
        </Card>

        {/* Right column */}
        <View style={{ flexGrow: 1, flexBasis: 320, minWidth: 300, gap: 18 }}>
          {/* Channel caps */}
          <Card>
            <CardHead>
              <SectionHead icon="chart-line" title="Channel caps" />
            </CardHead>
            <CardBody>
              <Stack gap={14}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <Txt size={FS.md} weight={FW.medium} color={C.ink}>
                      Voice (SUKI)
                    </Txt>
                    <Txt size={12.5} color={C.ink3}>
                      one suggestion keeps the turn natural
                    </Txt>
                  </View>
                  <Stepper value={settings.voice_max} onChange={(v) => persist({ voice_max: v }, `Voice cap set to ${v}`)} />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <View style={{ flex: 1 }}>
                    <Txt size={FS.md} weight={FW.medium} color={C.ink}>
                      Screen
                    </Txt>
                    <Txt size={12.5} color={C.ink3}>
                      cards can show a couple
                    </Txt>
                  </View>
                  <Stepper value={settings.screen_max} onChange={(v) => persist({ screen_max: v }, `Screen cap set to ${v}`)} />
                </View>
              </Stack>
            </CardBody>
          </Card>

          {/* DPDP & retention */}
          <Card>
            <CardHead>
              <SectionHead icon="lock" title="DPDP & retention" />
            </CardHead>
            <CardBody>
              <Stack gap={14}>
                <View style={{ gap: 4 }}>
                  <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                    Raw-ledger retention window
                  </Txt>
                  <Select
                    value={String(settings.pii_retention_days)}
                    onChange={(v) => persist({ pii_retention_days: Number(v) }, `Retention set to ${v} days`)}
                    options={RETENTION_OPTIONS}
                  />
                  <Txt size={FS.xs} color={C.ink3}>
                    Aggregate stats are de-identified and kept forever; only raw contact-linked events expire.
                  </Txt>
                </View>
                <View style={{ borderTopWidth: 1, borderTopColor: C.line, paddingTop: 12 }}>
                  <Txt size={FS.md} weight={FW.medium} color={C.ink} style={{ marginBottom: 3 }}>
                    Right to erasure
                  </Txt>
                  <Txt size={12.5} color={C.ink3} style={{ marginBottom: 10 }} lineHeight={18}>
                    Erase a customer’s PII from the raw ledger on request. Aggregates are unaffected.
                  </Txt>
                  <Button label="Erase a contact…" variant="danger" size="sm" onPress={() => setErasureOpen(true)} />
                </View>
              </Stack>
            </CardBody>
          </Card>

          {/* Advanced */}
          <Card>
            <CardHead right={<Chip label="Tier-2" />}>
              <SectionHead icon="gear" title="Advanced" />
            </CardHead>
            <CardBody>
              <View style={{ gap: 6 }}>
                <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                  Ignored-event weight in accept-rate:{' '}
                  <Txt mono size={FS.sm} color={C.ink2}>
                    {settings.ignored_weight.toFixed(1)}
                  </Txt>
                </Txt>
                <Slider value={settings.ignored_weight} onChange={(v) => persist({ ignored_weight: Math.round(v * 10) / 10 })} />
                <Txt size={FS.xs} color={C.ink3}>
                  0 = ignored excluded from the rate (the v0 default). Counts are always retained.
                </Txt>
              </View>
            </CardBody>
          </Card>
        </View>
      </View>

      {/* Preset preview modal */}
      <AppModal
        open={!!previewKey}
        onClose={closePreview}
        title={previewDetail?.name ?? 'Preset'}
        flushBody
        footer={
          <>
            <Txt size={12.5} color={C.ink4} style={{ flex: 1 }}>
              Every rule installs as active and is fully editable afterwards.
            </Txt>
            <Button label="Close" onPress={closePreview} />
            {previewKey && !installed.has(previewKey) && (
              <Button
                label={`Install all ${previewDetail?.rules.length ?? ''} rules`}
                variant="accent"
                onPress={() => install(previewKey, previewDetail?.name ?? 'Preset')}
              />
            )}
          </>
        }
      >
        <View style={{ flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 18, backgroundColor: C.paper2, borderBottomWidth: 1, borderBottomColor: C.line }}>
          <Eyebrow>Rule &amp; trigger</Eyebrow>
        </View>
        {(previewDetail?.rules ?? []).map((r, i) => {
          const m = motionMeta(r.motion);
          return (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 18, borderBottomWidth: 1, borderBottomColor: C.line }}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Txt size={14.5} weight={FW.semibold} color={C.ink}>
                  {r.name}
                </Txt>
                <Txt size={13} color={C.ink3} style={{ marginTop: 2 }}>
                  When: {r.trigger_summary} · pick by {STRATEGY_SHORT[r.select] ?? r.select}
                </Txt>
              </View>
              <Chip tone={m.tone} dot label={m.label} />
            </View>
          );
        })}
      </AppModal>

      {/* DPDP erasure modal */}
      <AppModal
        open={erasureOpen}
        onClose={() => setErasureOpen(false)}
        title="Erase contact PII"
        size="sm"
        footer={
          erasureDone ? undefined : (
            <>
              <Button label="Cancel" onPress={() => setErasureOpen(false)} />
              <Button label="Erase PII" variant="danger" disabled={!erasureContact.trim()} onPress={confirmErasure} />
            </>
          )
        }
      >
        {erasureDone ? (
          <View style={{ alignItems: 'center', paddingVertical: 14 }}>
            <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: C.successSoft, alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <Icon name="check" size={22} color={C.success} strokeWidth={2.4} />
            </View>
            <Txt size={15} weight={FW.semibold} color={C.ink}>
              PII erased
            </Txt>
            <Txt size={FS.md} color={C.ink3} align="center" style={{ marginTop: 4 }}>
              contact_id nulled, pii_purged set. Aggregate stats untouched.
            </Txt>
          </View>
        ) : (
          <Stack gap={12}>
            <Alert tone="warn" iconSize={16}>
              <Txt size={13.5} color={C.warn} lineHeight={19}>
                This is the only mutation the append-only ledger permits. It cannot be undone.
              </Txt>
            </Alert>
            <View style={{ gap: 4 }}>
              <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                Contact ID
              </Txt>
              <TextField value={erasureContact} onChangeText={setErasureContact} placeholder="ct_a1b2c3d4…" mono />
            </View>
            <Txt size={13} color={C.ink3} lineHeight={18}>
              All raw attach_event rows for this contact will have contact_id nulled and pii_purged=true.
            </Txt>
          </Stack>
        )}
      </AppModal>
    </Page>
  );
}
